import { Link } from "react-router-dom";
import type { Book } from "../api";
import { useT } from "../i18n";
import BookTile, { BookGrid, FinishedSeal } from "./BookTile";
import SeriesLine, { tileHref } from "./SeriesLine";
import { initialOf, rampFor } from "./cover";
import { PlayIcon } from "./icons";
import Logo from "./Logo";
import Placeholder from "./Placeholder";
import ProgressBar from "./ProgressBar";
import SaveButton from "./SaveButton";

/**
 * BookList is a grid of books with its loading, error and empty states — the
 * shape shared by the reader's own library and the catalogue at /browse
 * (ADR-0060). One component, because the two screens differ in which books
 * they hold and what an empty one means, never in how a book looks.
 */
export function BookList({
  books,
  isPending,
  error,
  onRetry,
  empty,
  savable = false,
  progress = false,
  seriesCount = false,
  testID = "book-list",
}: {
  books: Book[];
  isPending: boolean;
  error?: Error | null;
  onRetry: () => void;
  empty: React.ReactNode;
  savable?: boolean;
  /** Draw how far the viewer has got on each tile (ADR-0061); the shelf only. */
  progress?: boolean;
  /** A series shows once here, so its tile says how many volumes (0119). */
  seriesCount?: boolean;
  testID?: string;
}) {
  const t = useT();

  if (isPending) return <SkeletonList />;
  if (error)
    return <ErrorState message={error.message || t.common.somethingWrong} onRetry={onRetry} />;
  if (books.length === 0) return <>{empty}</>;

  // One book is a feature, not a grid of one: a lone half-width tile leaves
  // the rest of the screen empty.
  if (books.length === 1) {
    return (
      <ul data-testid={testID}>
        <FeaturedBook
          book={books[0]!}
          savable={savable}
          progress={progress}
          seriesCount={seriesCount}
        />
      </ul>
    );
  }

  return (
    <BookGrid testID={testID}>
      {books.map((book) => (
        <BookTile
          key={book.id}
          book={book}
          savable={savable}
          progress={progress}
          seriesCount={seriesCount}
        />
      ))}
    </BookGrid>
  );
}

/**
 * FeaturedBook is a lone book, given the width: cover, title, author and the
 * start of its description.
 */
function FeaturedBook({
  book,
  savable,
  progress,
  seriesCount,
}: {
  book: Book;
  savable: boolean;
  progress: boolean;
  seriesCount: boolean;
}) {
  const t = useT();
  const total = book.chapters_total ?? 0;
  const done = book.chapters_finished ?? 0;
  // The same rule as a tile's (BookTile): a bar only where there is something
  // to show. A lone book is the shelf too, so it gets one.
  const bar = progress && total > 0 && done > 0;
  return (
    <li data-testid="book-tile" className="relative">
      {savable && <SaveButton book={book} />}
      <Link
        to={tileHref(book, seriesCount)}
        className="group flex gap-4 rounded-card bg-surface p-3 shadow-[0_12px_32px_-24px_rgb(0_0_0/0.45)] transition-shadow hover:shadow-[0_16px_40px_-20px_rgb(0_0_0/0.45)]"
      >
        <Cover book={book} className="w-32 shrink-0 shadow-md sm:w-40" testID="tile-cover" />
        <div className="flex min-w-0 flex-1 flex-col py-1">
          <h2 dir="auto" className="line-clamp-2 text-lg leading-snug font-semibold">
            {book.title}
          </h2>
          {book.author && (
            <p dir="auto" className="mt-0.5 truncate text-sm text-muted">
              {book.author}
            </p>
          )}
          {book.series && (
            <SeriesLine
              series={book.series}
              count={seriesCount}
              testID="tile-series"
              className="mt-0.5 text-xs font-medium text-sage-deep"
            />
          )}
          {/* A book shared with a group this reader is in (ADR-0153). */}
          {book.shared_with_you && (
            <p
              data-testid="featured-shared-with-you"
              className="mt-0.5 text-xs font-medium text-sage-deep"
            >
              {t.library.sharedWithYou}
            </p>
          )}

          {book.description && (
            <p dir="auto" className="mt-2 line-clamp-3 text-sm leading-6 text-ink-soft">
              {book.description}
            </p>
          )}
          {progress && book.finished_at ? (
            <FinishedSeal at={book.finished_at} className="mt-3" />
          ) : (
            bar && (
              <ProgressBar
                className="mt-3 max-w-xs"
                percent={Math.round((Math.min(done, total) / total) * 100)}
                label={t.library.chaptersFinished(done, total)}
              />
            )
          )}
          {/* Listening only where there is something to hear; a book nobody
              has narrated is still there to read. */}
          <span
            data-testid="featured-action"
            className="mt-auto inline-flex items-center gap-1.5 self-start pt-3 text-sm font-medium text-sage-deep"
          >
            {(book.listen_ms ?? 0) > 0 ? (
              <>
                <PlayIcon className="size-3.5" />
                {t.book.startListening}
              </>
            ) : (
              t.book.startReading
            )}
          </span>
        </div>
      </Link>
    </li>
  );
}

/** Cover is a book's picture at any size, or its generated stand-in. */
export function Cover({
  book,
  className,
  testID,
}: {
  book?: Book;
  className: string;
  testID?: string;
}) {
  const [from, to] = rampFor(book?.id ?? "");
  return (
    <div className={`aspect-[3/4] overflow-hidden rounded-[0.6rem] bg-sunk ${className}`}>
      {book?.cover_url ? (
        <img
          src={book.cover_url}
          alt=""
          data-testid={testID}
          className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
        />
      ) : (
        book && (
          <div
            data-testid={testID ? "tile-placeholder" : undefined}
            className="grid size-full place-items-center text-2xl font-semibold text-white/85"
            style={{ background: `linear-gradient(150deg, ${from}, ${to})` }}
          >
            <span aria-hidden="true">{initialOf(book.title)}</span>
          </div>
        )
      )}
    </div>
  );
}

/**
 * The skeleton is the grid, in the grid's own shape. A list of grey bars while
 * a grid loads is a visible layout change at the moment the books arrive, and
 * that is the moment a thumb is already moving towards one.
 */
export function SkeletonList() {
  const t = useT();
  return (
    <ul
      aria-busy="true"
      aria-label={t.library.loadingBooks}
      data-testid="book-list-placeholder"
      className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 lg:grid-cols-4"
    >
      {[0, 1, 2, 3].map((i) => (
        <li key={i}>
          <Placeholder className="aspect-[3/4] rounded-card border border-line" />
          <Placeholder className="mt-2 h-3 w-3/4 rounded-full" />
        </li>
      ))}
    </ul>
  );
}

/** NothingPublished is the catalogue's empty state: nobody has published yet. */
export function NothingPublished() {
  const t = useT();
  return (
    <div
      data-testid="empty-state"
      className="rounded-card border border-dashed border-line-strong p-10 text-center"
    >
      <Logo size={30} className="mx-auto opacity-40" />
      <p className="mt-3 text-sm text-muted">{t.library.noBooks}</p>
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  const t = useT();
  return (
    <div
      role="alert"
      data-testid="error-state"
      className="rounded-card border border-red-200 bg-red-50 p-4 dark:border-red-900/60 dark:bg-red-950/50"
    >
      <p className="text-sm text-red-800 dark:text-red-200">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-3 rounded-control bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-red-700"
      >
        {t.common.tryAgain}
      </button>
    </div>
  );
}
